<template>
  <div class="fdsg-chart">
    <div class="fdsg-chart-loading" v-if="isLoading">Loading...</div>
    <div ref="chart" class="fdsg-chart-container"></div>
  </div>
</template>

<script>
import merge from "deepmerge";
import moment from "moment-timezone";
global.moment = moment;

// We import from src/content here, as we will import the required css in scss/vendor/_chartiq.scss
import { ChartEngineContent } from "@fds/wm-id-chart-iq-content/src/content";
import {
  ChartIQContentDataProvider,
  ChartIQContentDataProviderConfig
} from "@fds/wm-typescript-data-providers";

import { defaultTimeSpans } from "@/vue/util/chart";
import {
  mdg2Client,
  chartIQLatestPath,
  eventBus,
  locale,
  numberFormatter,
  dateFormatter
} from "../util/instances";
import * as utils from "../util/chart";
const logger = mdg2Client.getLogger();

/**
 * Format for price values
 *
 * @param value
 * @returns {string}
 */
const formatValue = value => {
  return numberFormatter.format(value, "BIG_NUMBER");
};
/**
 * Format for date time
 *
 * @param value
 * @param intraday true or false
 * @returns {string}
 */
const formatDateTime = (value, intraday) => {
  return dateFormatter.format(value, intraday ? "DATETIME-SHORT" : "DATE");
};

const { ScriptLoader } = utils;
const scriptLoader = new ScriptLoader({
  src: chartIQLatestPath,
  global: "CIQ"
});

const emptyObject = function() {
  return {};
};

const defaultChartConfig = {
  chartType: "line",
  spanBase: "today",
  spanMultiplier: 1,
  maintainPeriodicity: true
};

export default {
  name: "BasicChart",
  props: {
    channel: {
      type: String,
      required: true,
      default: ""
    },
    idNotation: {
      type: Number,
      required: true
    },
    selectedSpan: {
      type: String,
      required: false,
      default: "1D"
    },
    chartSettingsKey: {
      type: String,
      required: false,
      default: "myChartLayout"
    },
    config: {
      type: Object,
      required: false,
      default: emptyObject
    },
    theme: {
      type: Object,
      required: false,
      default: emptyObject
    }
  },
  data() {
    return {
      isLoading: true,
      error: false,
      isConnected: mdg2Client.isConnected
    };
  },
  methods: {
    getInitialConfig() {
      const selected = defaultTimeSpans.find(
        obj => obj.label === this.selectedSpan
      );
      const base = selected ? utils[selected.span].base : "today";
      const multiplier = selected ? utils[selected.span].multiplier : 1;
      const interval = selected ? utils[selected.span].periodicity.interval : 1;
      const periodicity = selected
        ? utils[selected.span].periodicity.period
        : 1;
      const timeUnit = selected ? utils[selected.span].periodicity.timeUnit : 1;
      return {
        spanBase: base,
        spanMultiplier: multiplier,
        interval: interval,
        periodicity: periodicity,
        maintainPeriodicity: base === "today" || base === "year",
        timeUnit: timeUnit
      };
    },
    addBenchmark(idNotation, color) {
      if (idNotation) {
        this.engine.addBenchmark(idNotation, color);
      }
    },
    clearBenchmarks() {
      for (const field in this.engine.stxx.chart.series) {
        this.engine.stxx.removeSeries(field);
      }
      this.dataProvider.destroyBenchmarkSubscriptions();
    },
    /**
     * Set time span ("INTRADAY" | "FIVE_DAYS" | "ONE_MONTH")
     */
    setTimeSpan(span) {
      this.engine.stxx.dontRoll = !(
        typeof utils[span].dontRoll === "undefined" ||
        utils[span].dontRoll === false
      );
      this.engine.stxx.setSpan(utils[span]);
    },
    /**
     * Notation Event Subscription
     */
    initNotationEventBus() {
      const engine = this.engine;
      const event = `${this.channel}:changeNotation`;
      if (this.channel) {
        eventBus.$on(event, function(notation) {
          engine.idNotation = notation;
        });
      }
    },
    /**
     * Timespan Event Subscription
     */
    initTimeSpanEventBus() {
      const setTimeSpan = this.setTimeSpan;
      const event = `${this.channel}:changeTimeSpan`;
      if (this.channel) {
        eventBus.$on(event, function(timespan) {
          setTimeSpan(timespan.span);
        });
      }
    },
    /**
     * Benchmark Event Subscription
     */
    initBenchmarkEventBus() {
      const event = `${this.channel}:changeBenchmark`;
      const clearBenchmarks = this.clearBenchmarks;
      const addBenchmark = this.addBenchmark;
      if (this.channel) {
        eventBus.$on(event, function(benchmark) {
          clearBenchmarks();
          if (benchmark) {
            console.log(benchmark.notation, benchmark.color);
            addBenchmark(benchmark.notation, benchmark.color);
          }
        });
      }
    },
    /**
     * Theme options
     * @see https://documentation.chartiq.com/CIQ.ThemeHelper.html#settings
     */
    setTheme() {
      if (this.theme) {
        const helper = new this.CIQ.ThemeHelper({ stx: this.engine.stxx });
        helper.settings = merge(helper.settings, this.theme);
        helper.update();
      }
    },
    initEngine() {
      const initialConfig = merge(defaultChartConfig, this.getInitialConfig());
      const config = this.config
        ? merge(initialConfig, this.config)
        : initialConfig;

      this.engine = new ChartEngineContent(this.$refs.chart, config);
      this.engine.attachDataHandler(this.dataProvider);
      this.setTheme();
    },
    async render() {
      try {
        this.isLoading = true;
        await this.engine.init({
          idNotation: this.idNotation,
          chartSettingsKey: this.chartSettingsKey,
          language: locale,
          theme: "light",
          defaultPriceRelativeInstrument: this.idNotation
        });
      } catch (err) {
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    }
  },
  created() {
    // Clear local layout storage
    if (window.localStorage) {
      window.localStorage.removeItem(this.chartSettingsKey);
    }
    const apiVersion = process.env.VUE_APP_ENDPOINT_API_VERSION || "v1";
    logger.debug("VUE_APP_ENDPOINT_API_VERSION", apiVersion);
    const dataProviderConfig = {
      ...new ChartIQContentDataProviderConfig(apiVersion),
      API_PRICES_QUALITY: "DLY",
      FUNC_FORMAT_VALUE: formatValue,
      FUNC_FORMAT_DATE_TIME: formatDateTime
    };
    this.dataProvider = new ChartIQContentDataProvider(
      mdg2Client,
      dataProviderConfig
    );
  },
  async mounted() {
    this.CIQ = await scriptLoader.load();
    // @see https://documentation.chartiq.com/CIQ.I18N.html#-static-translateUI__anchor
    // Prevent this translating as it will translate from body
    this.CIQ.I18N.translateUI = () => {};
    this.initEngine();
    this.render();
    this.initNotationEventBus();
    this.initTimeSpanEventBus();
    this.initBenchmarkEventBus();
  },
  watch: {
    selectedSpan: function(timespan) {
      const selected = defaultTimeSpans.find(obj => obj.label === timespan);
      this.setTimeSpan(selected.span);
    },
    config: function() {
      // TODO: Update chart if config changes
      // 1. Destroy chart
      // 2. Init chart with new config
    },
    theme: function() {
      this.setTheme();
      this.engine.stxx.draw();
    },
    idNotation: function(notation) {
      this.engine.idNotation = notation;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../assets/styles/index";
.fdsg-chart {
  position: relative;
  font-family: "Roboto", sans-serif !important;
  .fdsg-chart-loading {
    font-size: 1.6rem;
    position: absolute;
    width: 100%;
    height: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .fdsg-chart-container {
    position: relative;
    min-height: 400px;
  }
}
</style>
